import PropTypes from "prop-types";
import { Dialog } from "@mui/material";
import { useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AppSnackbar from "layouts/notifications/Snackbar";
import { deleteCrl } from "services/crlService";
import { useTranslation } from "hooks/useTranslation";
import { translateApiMessage } from "utils/translateApiMessage";

export default function DeleteCrl({ open, onClose, crl, onSuccess, title }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });

  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false, type: "", message: "" }));
  };

  const handleCancel = () => {
    if (loading) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!crl?.id) return;

    setLoading(true);
    try {
      const result = await deleteCrl(crl.id);
      if (result.status === "success") {
        let message = t("crls.notifications.crlDeleted", "Data collector deleted successfully");
        if (result.data?.linkedExams > 0) {
          message = t(
            "crls.notifications.crlDeletedWithExams",
            "Data collector deleted. Historical exam records are preserved."
          );
        }
        showSnackbar("success", message);
        setTimeout(() => {
          onClose();
          onSuccess?.(message);
        }, 800);
        return;
      }

      showSnackbar(
        "error",
        translateApiMessage(
          result.message,
          result.code,
          t,
          t("crls.notifications.crlDeleteFailed", "Failed to delete data collector")
        )
      );
    } catch (error) {
      console.error("Failed to delete CRL:", error);
      showSnackbar(
        "error",
        t("crls.notifications.crlDeleteFailed", "Failed to delete data collector")
      );
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = title || t("crls.modals.deleteCrl", "Delete Data Collector");
  const crlName = crl?.name || crl?.username || "";

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
        <MDBox p={3} textAlign="center">
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            {modalTitle}
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={3}>
            {t(
              "crls.modals.deleteConfirm",
              "Are you sure you want to delete {{name}}? This action cannot be undone.",
              { name: crlName }
            )}
          </MDTypography>
          <MDBox display="flex" justifyContent="center" gap={2}>
            <MDButton variant="gradient" color="error" onClick={handleConfirm} disabled={loading}>
              {t("crls.buttons.proceed", "Proceed")}
            </MDButton>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              {t("crls.buttons.cancel", "Cancel")}
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

DeleteCrl.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  crl: PropTypes.object,
  onSuccess: PropTypes.func,
  title: PropTypes.string,
};

DeleteCrl.defaultProps = {
  crl: null,
  onSuccess: undefined,
  title: null,
};
